import React, { useState, useEffect, useCallback } from 'react';
import { 
  Radio as RadioIcon, RefreshCw, AlertCircle, Clock, 
  Users, TrendingUp, BarChart3, Headphones, Settings,
  Edit, Save, X, Search, Filter, Wifi, WifiOff,
  Music, MapPin, Hash, CheckCircle, XCircle, Info,
  ChevronDown, ChevronUp, Play, Pause, Volume2, Plus, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AAR_CHANNELS, formatChannel } from '@/lib/aarChannels';

// Constants for feed groups
const FEED_GROUPS = {
  railstream: { 
    id: 'railstream', 
    name: 'RailStream', 
    color: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
    description: '27 primary feeds'
  },
  serfa: { 
    id: 'serfa', 
    name: 'SERFA', 
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    description: 'Slots 100-199'
  },
  nerfa: { 
    id: 'nerfa', 
    name: 'NERFA', 
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    description: 'Slots 400-499'
  }
};

const APP_OPTIONS = [
  { id: 'railstream', name: 'RailStream', description: 'Video + Radio app' },
  { id: 'railfeeds', name: 'RailFeeds', description: 'Radio-only app' }
];

// Determine feed group from stream path
const getFeedGroup = (streamPath) => {
  if (!streamPath) return 'railstream';
  const path = streamPath.toUpperCase();
  if (path.includes('SERFA')) return 'serfa';
  if (path.includes('NERFA')) return 'nerfa';
  return 'railstream';
};

// Format uptime
const formatUptime = (seconds) => {
  if (!seconds) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

// Format hours
const formatHours = (hours) => {
  if (hours == null || isNaN(hours)) return '0.0';
  return Number(hours).toFixed(hours >= 10 ? 1 : 2);
};

// Skeleton loader
const SkeletonRow = () => (
  <TableRow>
    <TableCell><div className="h-4 w-16 animate-pulse rounded bg-muted" /></TableCell>
    <TableCell><div className="h-4 w-32 animate-pulse rounded bg-muted" /></TableCell>
    <TableCell><div className="h-4 w-12 animate-pulse rounded bg-muted" /></TableCell>
    <TableCell><div className="h-4 w-16 animate-pulse rounded bg-muted" /></TableCell>
    <TableCell><div className="h-4 w-20 animate-pulse rounded bg-muted" /></TableCell>
  </TableRow>
);

// Stream status indicator
const StreamStatus = ({ status, listeners }) => {
  const isOnline = status === 1;
  return (
    <div className="flex items-center gap-2">
      <div className={`h-2 w-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
      <span className={isOnline ? 'text-green-400' : 'text-muted-foreground'}>
        {isOnline ? `${listeners} listening` : 'Offline'}
      </span>
    </div>
  );
};

// AAR Channel Multi-Select Component
const AARChannelPicker = ({ selected = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  const filteredChannels = AAR_CHANNELS.filter(ch => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      ch.channel.toString().includes(q) ||
      ch.frequency.includes(q) ||
      ch.use.toLowerCase().includes(q) ||
      ch.notes.toLowerCase().includes(q)
    );
  });

  const toggleChannel = (channelNum) => {
    if (selected.includes(channelNum)) {
      onChange(selected.filter(c => c !== channelNum));
    } else {
      onChange([...selected, channelNum]);
    }
  };

  return (
    <div className="space-y-2">
      <div 
        className="min-h-[40px] p-2 border border-border rounded-md bg-secondary/30 cursor-pointer flex flex-wrap gap-1"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected.length === 0 ? (
          <span className="text-muted-foreground text-sm">Select AAR channels...</span>
        ) : (
          selected.map(ch => {
            const channel = AAR_CHANNELS.find(c => c.channel === ch);
            return (
              <Badge 
                key={ch} 
                variant="secondary" 
                className="text-xs"
                onClick={(e) => { e.stopPropagation(); toggleChannel(ch); }}
              >
                Ch {ch} ({channel?.frequency || '?'})
                <X className="ml-1 h-3 w-3" />
              </Badge>
            );
          })
        )}
      </div>
      
      {isOpen && (
        <div className="border border-border rounded-md bg-card p-2 max-h-60 overflow-y-auto">
          <Input
            placeholder="Search channels..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2 h-8"
          />
          <div className="space-y-1">
            {filteredChannels.slice(0, 30).map(ch => (
              <div 
                key={ch.channel}
                className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-secondary/50 ${
                  selected.includes(ch.channel) ? 'bg-primary/20' : ''
                }`}
                onClick={() => toggleChannel(ch.channel)}
              >
                <div className="flex items-center gap-2">
                  <div className={`h-4 w-4 border rounded flex items-center justify-center ${
                    selected.includes(ch.channel) ? 'bg-primary border-primary' : 'border-border'
                  }`}>
                    {selected.includes(ch.channel) && <CheckCircle className="h-3 w-3 text-white" />}
                  </div>
                  <span className="text-sm font-mono">Ch {ch.channel}</span>
                  <span className="text-xs text-muted-foreground">{ch.frequency} MHz</span>
                </div>
                <Badge variant="outline" className="text-xs">{ch.use}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Edit Feed Dialog
const EditFeedDialog = ({ feed, open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    aar_channels: [],
    state: '',
    apps: ['railstream'],
    enabled: true,
    sort: 0
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (feed) {
      setFormData({
        name: feed.name || feed.servertitle || '',
        description: feed.description || '',
        aar_channels: feed.aar_channels || [],
        state: feed.state || '',
        apps: feed.apps || ['railstream'],
        enabled: feed.enabled !== false,
        sort: feed.sort || 0
      });
    }
  }, [feed]);

  const handleSave = async () => {
    console.log('handleSave called!', { feed, formData });
    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        code: feed?.code || feed?.streampath?.replace('/', '') || '',
        streampath: feed?.streampath || `/${feed?.code || ''}`
      });
    } catch (err) {
      console.error('handleSave error:', err);
      toast.error(err.message || 'Failed to save feed');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleApp = (appId) => {
    if (formData.apps.includes(appId)) {
      if (formData.apps.length > 1) {
        setFormData(prev => ({ ...prev, apps: prev.apps.filter(a => a !== appId) }));
      }
    } else {
      setFormData(prev => ({ ...prev, apps: [...prev.apps, appId] }));
    }
  };

  if (!feed) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-primary" />
            Edit Radio Feed
          </DialogTitle>
          <DialogDescription>
            Configure settings for {feed.streampath || feed.code}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Stream Info (Read-only) */}
          <div className="p-3 rounded-lg bg-secondary/30 border border-border">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Stream Path:</span>
                <p className="font-mono font-medium">{feed.streampath || 'N/A'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <StreamStatus status={feed.streamstatus} listeners={feed.currentlisteners} />
              </div>
              <div>
                <span className="text-muted-foreground">Bitrate:</span>
                <p className="font-medium">{feed.bitrate || 'N/A'} kbps</p>
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Fostoria, OH"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))}
                placeholder="e.g., OH"
                maxLength={2}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="e.g., NS & CSX diamond crossing near downtown"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>AAR Radio Channels</Label>
            <AARChannelPicker
              selected={formData.aar_channels}
              onChange={(channels) => setFormData(prev => ({ ...prev, aar_channels: channels }))}
            />
            <p className="text-xs text-muted-foreground">Select railroad frequencies monitored by this feed</p>
          </div>

          <div className="space-y-2">
            <Label>Available In Apps</Label>
            <div className="flex gap-2">
              {APP_OPTIONS.map(app => (
                <Button
                  key={app.id}
                  type="button"
                  variant={formData.apps.includes(app.id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleApp(app.id)}
                  className="flex-1"
                >
                  {formData.apps.includes(app.id) && <CheckCircle className="mr-2 h-4 w-4" />}
                  {app.name}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <div>
              <p className="font-medium">Feed Enabled</p>
              <p className="text-sm text-muted-foreground">Show this feed in apps</p>
            </div>
            <Switch
              checked={formData.enabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Radio Component
export function Radio() {
  const [activeTab, setActiveTab] = useState('live');
  const [streams, setStreams] = useState([]);
  const [savedFeeds, setSavedFeeds] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({ streams: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  
  // Edit dialog
  const [editingFeed, setEditingFeed] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createFromStream, setCreateFromStream] = useState(null);
  
  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch live Shoutcast data via api service (environment-aware)
  const fetchShoutcastData = useCallback(async () => {
    try {
      const data = await api.getShoutcastStatistics();
      setStreams(data.streams || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Shoutcast fetch error:', err);
      setError(err.message || 'Failed to load live stream data');
    }
  }, []);

  // Fetch saved feeds from database
  const fetchSavedFeeds = useCallback(async () => {
    try {
      const data = await api.getRadioFeeds();
      setSavedFeeds(data.feeds || []);
    } catch (err) {
      console.error('Saved feeds fetch error:', err);
    }
  }, []);

  // Fetch analytics data
  const fetchAnalyticsData = useCallback(async () => {
    try {
      const data = await api.getRadioAnalytics(timeRange);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    }
  }, [timeRange]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchShoutcastData(), fetchAnalyticsData(), fetchSavedFeeds()]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchShoutcastData, fetchAnalyticsData, fetchSavedFeeds]);

  // Auto-refresh every 30 seconds for live tab
  useEffect(() => {
    if (activeTab !== 'live') return;
    const interval = setInterval(fetchShoutcastData, 30000);
    return () => clearInterval(interval);
  }, [activeTab, fetchShoutcastData]);

  // Refetch analytics when time range changes
  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalyticsData();
    }
  }, [timeRange, activeTab, fetchAnalyticsData]);

  // Filter streams
  const filteredStreams = streams.filter(stream => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesSearch = (
        stream.servertitle?.toLowerCase().includes(q) ||
        stream.streampath?.toLowerCase().includes(q) ||
        stream.songtitle?.toLowerCase().includes(q)
      );
      if (!matchesSearch) return false;
    }
    
    // Group filter
    if (groupFilter !== 'all') {
      const streamGroup = getFeedGroup(stream.streampath);
      if (streamGroup !== groupFilter) return false;
    }
    
    // Status filter
    if (statusFilter === 'online' && stream.streamstatus !== 1) return false;
    if (statusFilter === 'offline' && stream.streamstatus === 1) return false;
    if (statusFilter === 'listeners' && stream.currentlisteners === 0) return false;
    
    return true;
  });

  // Calculate stats
  const totalListeners = streams.reduce((sum, s) => sum + (s.currentlisteners || 0), 0);
  const onlineCount = streams.filter(s => s.streamstatus === 1).length;
  const groupCounts = {
    railstream: streams.filter(s => getFeedGroup(s.streampath) === 'railstream').length,
    serfa: streams.filter(s => getFeedGroup(s.streampath) === 'serfa').length,
    nerfa: streams.filter(s => getFeedGroup(s.streampath) === 'nerfa').length
  };

  // Check if a feed exists in database
  const isFeedSaved = (streamPath) => {
    const code = streamPath?.replace('/', '') || '';
    return savedFeeds.some(f => f.code === code);
  };

  // Handle create new feed - use PUT since it does upsert
  const handleCreateFeed = async (feedData) => {
    try {
      const code = feedData.code || feedData.streampath?.replace('/', '') || '';
      await api.updateRadioFeed(code, {
        name: feedData.name,
        description: feedData.description || '',
        state: feedData.state || '',
        aar_channels: feedData.aar_channels || [],
        apps: feedData.apps || ['railstream'],
        enabled: feedData.enabled !== false
      });
      toast.success(`Feed "${feedData.name}" added successfully`);
      setShowCreateDialog(false);
      setCreateFromStream(null);
      fetchSavedFeeds();
    } catch (err) {
      const errorMessage = typeof err === 'string' ? err : 
        (err?.message || err?.detail || 'Failed to add feed');
      toast.error(errorMessage);
      console.error('Create feed error:', err);
    }
  };

  // Handle save/update feed - PUT does upsert (creates if not exists)
  const handleSaveFeed = async (feedData) => {
    try {
      const code = feedData.code || feedData.streampath?.replace('/', '') || '';
      
      console.log('Saving feed:', { code, feedData });
      
      if (!code) {
        toast.error('Invalid feed - missing stream path');
        return;
      }
      
      const payload = {
        name: feedData.name,
        description: feedData.description,
        state: feedData.state,
        aar_channels: feedData.aar_channels,
        apps: feedData.apps,
        enabled: feedData.enabled
      };
      
      console.log('PUT payload:', payload);
      
      const result = await api.updateRadioFeed(code, payload);
      console.log('Save result:', result);
      
      toast.success(`Feed "${feedData.name}" saved`);
      fetchSavedFeeds();
      setEditingFeed(null);
    } catch (err) {
      console.error('Save feed error:', err);
      const errorMessage = typeof err === 'string' ? err : 
        (err?.message || err?.detail || 'Failed to save feed');
      toast.error(errorMessage);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (streamId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(streamId)) {
      newExpanded.delete(streamId);
    } else {
      newExpanded.add(streamId);
    }
    setExpandedRows(newExpanded);
  };

  // Analytics chart data
  const chartData = (analyticsData.streams || [])
    .slice(0, 10)
    .map(s => ({
      name: s.stream_id,
      hours: s.listen_hours || 0,
      title: s.title || s.stream_id
    }));

  return (
    <div className="space-y-6" data-testid="radio-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <RadioIcon className="h-8 w-8 text-primary" />
            Radio Management
          </h1>
          <p className="text-muted-foreground">
            Manage {streams.length} radio feeds across RailStream, SERFA, and NERFA
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground hidden sm:block">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchShoutcastData(); fetchAnalyticsData(); }}
            data-testid="refresh-radio-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Listeners</p>
                <p className="text-2xl font-bold text-primary">{totalListeners}</p>
              </div>
              <Headphones className="h-8 w-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Streams Online</p>
                <p className="text-2xl font-bold text-green-400">{onlineCount} / {streams.length}</p>
              </div>
              <Wifi className="h-8 w-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">RailStream</p>
                <p className="text-2xl font-bold text-orange-400">{groupCounts.railstream}</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                <RadioIcon className="h-4 w-4 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Partner Feeds</p>
                <p className="text-2xl font-bold text-blue-400">{groupCounts.serfa + groupCounts.nerfa}</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Users className="h-4 w-4 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="live" className="data-[state=active]:bg-primary">
            <Wifi className="mr-2 h-4 w-4" />
            Live Status
          </TabsTrigger>
          <TabsTrigger value="manage" className="data-[state=active]:bg-primary">
            <Settings className="mr-2 h-4 w-4" />
            Feed Management
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-primary">
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Live Status Tab */}
        <TabsContent value="live" className="space-y-4">
          {/* Filters */}
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, path, or now playing..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-streams-input"
                />
              </div>
              <Select value={groupFilter} onValueChange={setGroupFilter}>
                <SelectTrigger className="w-[160px]" data-testid="group-filter">
                  <SelectValue placeholder="All Groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  <SelectItem value="railstream">RailStream</SelectItem>
                  <SelectItem value="serfa">SERFA</SelectItem>
                  <SelectItem value="nerfa">NERFA</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="online">Online Only</SelectItem>
                  <SelectItem value="offline">Offline Only</SelectItem>
                  <SelectItem value="listeners">With Listeners</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Streams Table */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Volume2 className="h-5 w-5 text-primary" />
                  Live Streams ({filteredStreams.length})
                </span>
                <div className="flex gap-2">
                  {Object.entries(FEED_GROUPS).map(([key, group]) => (
                    <Badge 
                      key={key} 
                      variant="outline" 
                      className={`${group.color} cursor-pointer`}
                      onClick={() => setGroupFilter(groupFilter === key ? 'all' : key)}
                    >
                      {group.name}: {groupCounts[key]}
                    </Badge>
                  ))}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Table>
                  <TableBody>
                    {[1,2,3,4,5].map(i => <SkeletonRow key={i} />)}
                  </TableBody>
                </Table>
              ) : error ? (
                <div className="flex items-center justify-center py-8 text-destructive">
                  <AlertCircle className="mr-2 h-5 w-5" />
                  {error}
                </div>
              ) : filteredStreams.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RadioIcon className="h-12 w-12 mb-4 opacity-30" />
                  <p>No streams match your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Stream</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Listeners</TableHead>
                        <TableHead>Peak</TableHead>
                        <TableHead>Now Playing</TableHead>
                        <TableHead>Uptime</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStreams.map((stream) => {
                        const group = getFeedGroup(stream.streampath);
                        const isExpanded = expandedRows.has(stream.id);
                        
                        return (
                          <React.Fragment key={stream.id}>
                            <TableRow 
                              className="hover:bg-secondary/30 cursor-pointer"
                              onClick={() => toggleRowExpansion(stream.id)}
                              data-testid={`stream-row-${stream.id}`}
                            >
                              <TableCell>
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={FEED_GROUPS[group].color}>
                                    {group.toUpperCase()}
                                  </Badge>
                                  <div>
                                    <p className="font-medium">{stream.servertitle || 'Untitled'}</p>
                                    <p className="text-xs text-muted-foreground font-mono">{stream.streampath}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <StreamStatus status={stream.streamstatus} listeners={stream.currentlisteners} />
                              </TableCell>
                              <TableCell>
                                <span className="font-mono font-medium">{stream.currentlisteners || 0}</span>
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-muted-foreground">{stream.peaklisteners || 0}</span>
                              </TableCell>
                              <TableCell className="max-w-[200px]">
                                <p className="truncate text-sm text-muted-foreground" title={stream.songtitle}>
                                  {stream.songtitle || '—'}
                                </p>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-muted-foreground">
                                  {formatUptime(stream.streamuptime)}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    // Merge Shoutcast data with saved data from database
                                    const code = stream.streampath?.replace('/', '') || '';
                                    const savedData = savedFeeds.find(f => f.code === code);
                                    setEditingFeed({
                                      ...stream,
                                      // Override with saved data if it exists
                                      name: savedData?.name || stream.servertitle || '',
                                      description: savedData?.description || '',
                                      state: savedData?.state || '',
                                      aar_channels: savedData?.aar_channels || [],
                                      apps: savedData?.apps || ['railstream'],
                                      enabled: savedData?.enabled !== false
                                    });
                                  }}
                                  data-testid={`edit-stream-${stream.id}`}
                                  title="Edit feed settings"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                            
                            {/* Expanded Row Details */}
                            {isExpanded && (
                              <TableRow className="bg-secondary/20">
                                <TableCell colSpan={8} className="p-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <p className="text-muted-foreground">Bitrate</p>
                                      <p className="font-medium">{stream.bitrate || 'N/A'} kbps</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Sample Rate</p>
                                      <p className="font-medium">{stream.samplerate || 'N/A'} Hz</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Unique Listeners</p>
                                      <p className="font-medium">{stream.uniquelisteners || 0}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Total Hits</p>
                                      <p className="font-medium">{stream.streamhits?.toLocaleString() || 0}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Avg Listen Time</p>
                                      <p className="font-medium">{formatUptime(stream.averagetime)}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Genre</p>
                                      <p className="font-medium">{stream.servergenre || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Content Type</p>
                                      <p className="font-medium font-mono text-xs">{stream.content || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">Max Listeners</p>
                                      <p className="font-medium">{stream.maxlisteners || 'N/A'}</p>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feed Management Tab */}
        <TabsContent value="manage" className="space-y-4">
          <Card className="border-border bg-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary" />
                    Saved Radio Feeds ({savedFeeds.length})
                  </CardTitle>
                  <CardDescription>
                    Manage radio feed metadata - name, description, AAR channels, and app assignments
                  </CardDescription>
                </div>
                <Button onClick={() => setShowCreateDialog(true)} data-testid="add-feed-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Feed
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {savedFeeds.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Database className="h-12 w-12 text-muted-foreground/30 mb-4" />
                  <p className="text-lg font-medium">No Feeds in Database</p>
                  <p className="text-sm text-muted-foreground mb-4">Add your first radio feed to get started</p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add First Feed
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>AAR Channels</TableHead>
                      <TableHead>Apps</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {savedFeeds.map((feed) => (
                      <TableRow key={feed.code || feed._id}>
                        <TableCell className="font-mono font-medium">{feed.code}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{feed.name || 'Untitled'}</p>
                            {feed.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{feed.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{feed.state || '—'}</TableCell>
                        <TableCell>
                          {feed.aar_channels?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {feed.aar_channels.slice(0, 3).map(ch => (
                                <Badge key={ch} variant="secondary" className="text-xs">Ch {ch}</Badge>
                              ))}
                              {feed.aar_channels.length > 3 && (
                                <Badge variant="outline" className="text-xs">+{feed.aar_channels.length - 3}</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {(feed.apps || []).map(app => (
                              <Badge key={app} variant="outline" className="text-xs">{app}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {feed.enabled !== false ? (
                            <Badge className="bg-green-500/20 text-green-400">Enabled</Badge>
                          ) : (
                            <Badge variant="secondary">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingFeed({
                              ...feed,
                              streampath: feed.stream_path || `/${feed.code}`
                            })}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-medium">Listening Analytics</h3>
                <p className="text-sm text-muted-foreground">Historical data from user sessions</p>
              </div>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-32" data-testid="analytics-time-range">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">24 Hours</SelectItem>
                  <SelectItem value="7d">7 Days</SelectItem>
                  <SelectItem value="30d">30 Days</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Listen Hours</p>
                    <p className="text-2xl font-bold text-primary">
                      {formatHours((analyticsData.streams || []).reduce((sum, s) => sum + (s.listen_hours || 0), 0))}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-primary/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Sessions</p>
                    <p className="text-2xl font-bold text-orange-400">
                      {(analyticsData.streams || []).reduce((sum, s) => sum + (s.session_count || 0), 0)}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-orange-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Feeds</p>
                    <p className="text-2xl font-bold text-green-400">
                      {(analyticsData.streams || []).length}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Top Feeds by Listen Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number" 
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(v) => `${v}h`}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      width={70}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px'
                      }}
                      formatter={(value) => [`${formatHours(value)}h`, 'Listen Hours']}
                    />
                    <Bar dataKey="hours" fill="hsl(262, 83%, 58%)" radius={[0, 4, 4, 0]} maxBarSize={25} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Feed Dialog */}
      <EditFeedDialog
        feed={editingFeed}
        open={!!editingFeed}
        onClose={() => setEditingFeed(null)}
        onSave={handleSaveFeed}
      />

      {/* Create Feed Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-500" />
              Add Feed to Database
            </DialogTitle>
            <DialogDescription>
              Save this Shoutcast stream to the database so it appears in the apps
            </DialogDescription>
          </DialogHeader>
          
          {createFromStream && (
            <CreateFeedForm 
              stream={createFromStream} 
              onSave={handleCreateFeed}
              onCancel={() => {
                setShowCreateDialog(false);
                setCreateFromStream(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Create Feed Form Component
function CreateFeedForm({ stream, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: stream?.servertitle || '',
    description: '',
    state: '',
    aar_channels: [],
    apps: ['railstream'],
    enabled: true
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name) {
      return;
    }
    setIsSaving(true);
    await onSave({
      ...formData,
      code: stream?.streampath?.replace('/', '') || '',
      streampath: stream?.streampath || ''
    });
    setIsSaving(false);
  };

  const toggleApp = (appId) => {
    if (formData.apps.includes(appId)) {
      if (formData.apps.length > 1) {
        setFormData(prev => ({ ...prev, apps: prev.apps.filter(a => a !== appId) }));
      }
    } else {
      setFormData(prev => ({ ...prev, apps: [...prev.apps, appId] }));
    }
  };

  return (
    <div className="space-y-4 py-4">
      {/* Stream Info */}
      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Stream Path:</span>
            <p className="font-mono font-medium">{stream?.streampath || 'N/A'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Current Title:</span>
            <p className="font-medium">{stream?.servertitle || 'Untitled'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Status:</span>
            <p className="font-medium">{stream?.streamstatus === 1 ? '🟢 Online' : '⚫ Offline'}</p>
          </div>
        </div>
      </div>

      {/* Editable Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="create_name">Display Name *</Label>
          <Input
            id="create_name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Fostoria, OH"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="create_state">State</Label>
          <Input
            id="create_state"
            value={formData.state}
            onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))}
            placeholder="e.g., OH"
            maxLength={2}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="create_description">Description</Label>
        <Textarea
          id="create_description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="e.g., NS & CSX diamond crossing near downtown"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>AAR Radio Channels</Label>
        <AARChannelPicker
          selected={formData.aar_channels}
          onChange={(channels) => setFormData(prev => ({ ...prev, aar_channels: channels }))}
        />
        <p className="text-xs text-muted-foreground">Select railroad frequencies monitored by this feed</p>
      </div>

      <div className="space-y-2">
        <Label>Available In Apps</Label>
        <div className="flex gap-2">
          {APP_OPTIONS.map(app => (
            <Button
              key={app.id}
              type="button"
              variant={formData.apps.includes(app.id) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleApp(app.id)}
              className="flex-1"
            >
              {formData.apps.includes(app.id) && <CheckCircle className="mr-2 h-4 w-4" />}
              {app.name}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
        <div>
          <p className="font-medium">Feed Enabled</p>
          <p className="text-sm text-muted-foreground">Show this feed in apps</p>
        </div>
        <Switch
          checked={formData.enabled}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSaving || !formData.name} className="bg-green-600 hover:bg-green-700">
          <Database className="mr-2 h-4 w-4" />
          {isSaving ? 'Adding...' : 'Add to Database'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default Radio;
